import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useSession } from "@/hooks/auth-hooks";
import { db } from "@/lib/instantdb";

type Workspace = {
	id: string;
	name: string;
	slug: string;
	role: string;
	isActive: boolean;
	memberId: string;
};

type McpKeySummary = {
	configured: boolean;
	keyPrefix: string | null;
	lastUsedAt: string | null;
	name: string | null;
	updatedAt: string | null;
};

interface DashboardContextValue {
	user: {
		id: string;
		name?: string | null;
		email?: string;
		image?: string | null;
	} | null;
	isPending: boolean;
	isLoading: boolean;
	workspace: Workspace | null;
	workspaces: Workspace[];
	isWorkspacesLoading: boolean;
	switchWorkspace: (workspaceId: string) => Promise<void>;
	isMcpConnected: boolean;
	isMcpLoading: boolean;
	balanceCents: number;
	isBalanceLoading: boolean;
}

export const DashboardContext =
	React.createContext<DashboardContextValue | null>(null);

export function useDashboardContext() {
	const context = React.useContext(DashboardContext);
	if (!context) {
		throw new Error(
			"useDashboardContext must be used within DashboardProvider",
		);
	}
	return context;
}

interface DashboardProviderProps {
	children: React.ReactNode;
}

export function DashboardProvider({ children }: DashboardProviderProps) {
	const { user, isPending } = useSession();

	const { data: membersData, isLoading: isMembersLoading } = db.useQuery(
		user?.id
			? {
					workspaceMembers: {
						$: { where: { "user.id": user.id } },
						workspace: {},
					},
				}
			: null,
	);

	const workspaces: Workspace[] = React.useMemo(() => {
		if (!membersData?.workspaceMembers) return [];
		return membersData.workspaceMembers
			.filter((m) => m.workspace != null)
			.map((m) => {
				const ws = m.workspace!;
				return {
					id: ws.id,
					name: ws.name,
					slug: ws.slug,
					role: m.role,
					isActive: m.isActive,
					memberId: m.id,
				};
			});
	}, [membersData]);

	const activeWorkspace = workspaces.find((ws) => ws.isActive) ?? workspaces[0] ?? null;

	const { data: userData } = db.useQuery(
		user?.id ? { users: { $: { where: { id: user.id } } } } : null,
	);
	const dbUser = userData?.users?.[0];

	const mcpKeyQuery = useQuery({
		enabled: !!user,
		queryFn: async () => {
			const res = await fetch("/api/mcp/key", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) throw new Error("Failed to fetch MCP status");
			return res.json() as Promise<{ key: McpKeySummary; keys: McpKeySummary[] }>;
		},
		queryKey: ["dashboard", "mcp-key"],
		refetchInterval: 10_000,
	});
	const balanceQuery = useQuery({
		enabled: !!user,
		queryFn: async () => {
			const res = await fetch("/api/mcp/credits", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) throw new Error("Failed to fetch balance");
			return res.json() as Promise<{ balance: number; balanceCents: number }>;
		},
		queryKey: ["dashboard", "balance"],
		refetchInterval: 15_000,
	});

	const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
	const lastSeen = dbUser?.agentLastSeenAt ?? mcpKeyQuery.data?.key?.lastUsedAt;
	const isMcpConnected = Boolean(lastSeen && new Date(lastSeen).getTime() > fiveMinutesAgo);

	const switchWorkspace = React.useCallback(
		async (workspaceId: string) => {
			const res = await fetch("/api/workspaces", {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
				},
				body: JSON.stringify({ workspaceId }),
			});
			if (!res.ok) throw new Error("Failed to switch workspace");
		},
		[],
	);

	const value: DashboardContextValue = {
		user: user
			? { id: user.id, name: user.name, email: user.email, image: user.image }
			: null,
		isPending,
		isLoading: isPending,
		workspace: activeWorkspace,
		workspaces,
		isWorkspacesLoading: isMembersLoading,
		switchWorkspace,
		isMcpConnected,
		isMcpLoading: mcpKeyQuery.isLoading,
		balanceCents: balanceQuery.data?.balanceCents ?? 0,
		isBalanceLoading: balanceQuery.isLoading,
	};

	return (
		<DashboardContext.Provider value={value}>
			{children}
		</DashboardContext.Provider>
	);
}
